import React from 'react';
import { Container, Typography, Grid, Card, CardContent, CardActions, Button, Box, Paper, Fade, Zoom } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TranslateIcon from '@mui/icons-material/Translate';
import SchoolIcon from '@mui/icons-material/School';
import QuizIcon from '@mui/icons-material/Quiz';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import LanguageIcon from '@mui/icons-material/Language';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

function Home() {
  const navigate = useNavigate();

  const features = [
    {
      title: 'Daily Word Practice',
      description: 'Learn a new word every day with examples and pronunciation',
      icon: <TranslateIcon sx={{ fontSize: 40 }} />,
      path: '/daily-word',
      color: '#2196F3'
    },
    {
      title: 'Flashcards',
      description: 'Create and review flashcards to memorize vocabulary',
      icon: <SchoolIcon sx={{ fontSize: 40 }} />,
      path: '/flashcards',
      color: '#4CAF50'
    },
    {
      title: 'Quizzes',
      description: 'Test your knowledge with interactive quizzes',
      icon: <QuizIcon sx={{ fontSize: 40 }} />,
      path: '/quizzes',
      color: '#FF9800'
    },
    {
      title: 'Streaks',
      description: 'Track your learning progress and maintain your streak',
      icon: <LocalFireDepartmentIcon sx={{ fontSize: 40 }} />,
      path: '/streaks',
      color: '#F44336'
    }
  ];

  const stats = [
    {
      title: 'Languages',
      value: '5+',
      icon: <LanguageIcon sx={{ fontSize: 32, color: '#2196F3' }} />,
      description: 'Available languages to learn'
    },
    {
      title: 'Daily Words',
      value: '10+',
      icon: <TranslateIcon sx={{ fontSize: 32, color: '#4CAF50' }} />,
      description: 'New words to learn daily'
    },
    {
      title: 'Success Rate',
      value: '95%',
      icon: <EmojiEventsIcon sx={{ fontSize: 32, color: '#FF9800' }} />,
      description: 'User satisfaction rate'
    },
    {
      title: 'Progress',
      value: '24/7',
      icon: <TrendingUpIcon sx={{ fontSize: 32, color: '#F44336' }} />,
      description: 'Track your learning journey'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Fade in={true} timeout={1000}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            Welcome to Language Learning Companion
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto',
              mb: 4,
              lineHeight: 1.6
            }}
          >
            Your personal language learning journey starts here. Master new languages with interactive tools, daily practice, and personalized learning paths.
          </Typography>
        </Box>
      </Fade>

      <Fade in={true} timeout={1500}>
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  textAlign: 'center',
                  height: '100%',
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  borderRadius: 2,
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                  }
                }}
              >
                {stat.icon}
                <Typography variant="h4" sx={{ fontWeight: 'bold', my: 1 }}>
                  {stat.value}
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {stat.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Fade>

      <Fade in={true} timeout={2000}>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  borderRadius: 2,
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                  <Box 
                    sx={{ 
                      display: 'inline-flex',
                      p: 2,
                      borderRadius: '50%',
                      bgcolor: `${feature.color}15`,
                      color: feature.color,
                      mb: 2
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 'bold',
                      color: feature.color
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ 
                      mb: 2,
                      lineHeight: 1.6
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button 
                    fullWidth 
                    variant="contained" 
                    onClick={() => navigate(feature.path)}
                    sx={{ 
                      bgcolor: feature.color,
                      '&:hover': {
                        bgcolor: feature.color,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                      },
                      transition: 'all 0.2s'
                    }}
                  >
                    Get Started
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Fade>
    </Container>
  );
}

export default Home; 